/** Pure helpers for the lighting layer (kept free of Phaser so they can be unit tested). */

import {
  type ClientWeatherCondition,
  isThemeKey,
  type ThemeKey,
  WEATHER_CONDITIONS,
} from '@veriverse/shared';

/** One rectangle of same-kind tiles (indoor or outdoor) for the shade. */
export interface ShadeRun {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly outdoor: boolean;
}

/** Splits each map row into runs of indoor and outdoor tiles, so the shade needs few rectangles. */
export function shadeRuns(mask: Uint8Array, width: number, height: number): ShadeRun[] {
  const runs: ShadeRun[] = [];
  for (let y = 0; y < height; y += 1) {
    let start = 0;
    for (let x = 1; x <= width; x += 1) {
      const current = mask[y * width + start] === 1;
      if (x === width || (mask[y * width + x] === 1) !== current) {
        runs.push({ x: start, y, width: x - start, outdoor: current });
        start = x;
      }
    }
  }
  return runs;
}

/**
 * Stars for a window: a few fixed points per window so they do not jump between updates.
 * Positions are fractions of the window size.
 */
export function windowStars(id: number, count: number): { fx: number; fy: number }[] {
  let seed = (id * 2654435761) >>> 0;
  const next = () => {
    seed = (seed * 1664525 + 1013904223) >>> 0;
    return seed / 0x100000000;
  };
  return Array.from({ length: count }, () => ({ fx: 0.1 + next() * 0.8, fy: 0.1 + next() * 0.8 }));
}

/** Dev only: `?vvTime=2026-12-21T15:00:00Z` previews the lighting at another moment. */
export function devTimeOverride(search: string, isDev: boolean): Date | undefined {
  if (!isDev) return undefined;
  const value = new URLSearchParams(search).get('vvTime');
  if (!value) return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

const PREVIEW_CONDITIONS: readonly ClientWeatherCondition[] = ['unknown', ...WEATHER_CONDITIONS];

/** Dev only: `?weather=snow` (the same parameter the welcome splash reads) previews a condition (with 2 mm/h and 4 m/s wind). */
export function devWeatherOverride(
  search: string,
  isDev: boolean,
): ClientWeatherCondition | undefined {
  if (!isDev) return undefined;
  const value = new URLSearchParams(search).get('weather');
  return PREVIEW_CONDITIONS.find((condition) => condition === value);
}

/** `?theme=christmas` (or `off`) previews a seasonal theme locally; cosmetic, so any build. */
export function themeParamOverride(search: string): ThemeKey | 'off' | undefined {
  const value = new URLSearchParams(search).get('theme');
  if (value === 'off') return 'off';
  return isThemeKey(value) ? value : undefined;
}
